import { createContext, ReactNode, useContext, useState } from 'react';
import invoicesSelector from '../selectors/invoicesSelector';
import ViewInvoicesModal from '../components/ViewInvoicesModal';

type InvoicesContextProviderProps = {
  children: ReactNode;
  getPaymentInvoice: any;
  notify: (args: {
    content: string;
    type: 'info' | 'error' | 'success';
  }) => void;
};

type VideoDetailsType = {
  videoId: string;
  organisationId: string;
  outputs: Array<VideoOutput>;
  orderId?: string;
  isUrl?: boolean;
  readableId: string;
  name: string;
  completedAt: string;
};

const defaults = {
  state: {
    invoices: [],
    showInvoicesModal: false
  },
  actions: {
    openInvoices: () => void 0,
    closeModal: () => void 0
  }
};

const InvoicesContext = createContext(defaults);
export default InvoicesContext;
export function useInvoicesContext() {
  return useContext(InvoicesContext);
}

export function InvoicesContextProvider({
  children,
  getPaymentInvoice,
  notify
}: InvoicesContextProviderProps) {
  const [video, setVideo] = useState({} as VideoDetailsType);
  const [invoices, setInvoices] = useState([]);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);

  const handleModalOpen = (mInvoices: Array<{ invoiceUrl: string }>) => {
    // When no invoices just return
    if (!mInvoices.length) return;

    if (mInvoices.length === 1) {
      const { invoiceUrl } = mInvoices[0];

      if (!invoiceUrl) return;

      window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setShowInvoicesModal(true);
  };

  const openInvoices = async (mVideo: VideoDetailsType) => {
    const { videoId, organisationId, orderId } = mVideo;
    if (!organisationId && !(videoId || orderId)) return;
    // check if the function was called with different params
    const { isUrl = true } = mVideo;
    const { data, error } = await getPaymentInvoice({
      videoId,
      organisationId,
      orderId,
      isUrl
    });

    if (!data || error) return;

    const { getPaymentInvoice: invoiceData } = data;
    if (!invoiceData.length) {
      notify({ content: 'No invoices found', type: 'info' });
      return;
    }
    setInvoices(invoiceData);
    setVideo(mVideo);

    // handle opening of invoice
    handleModalOpen(invoiceData);
  };

  const closeModal = () => {
    setShowInvoicesModal(false);
  };

  function getCompletedAt(outputs: Array<VideoOutput>) {
    if (!outputs || outputs.length === 0) return null;
    return new Date(
      Number(outputs[outputs.length - 1].completedAt)
    ).toISOString();
  }

  return (
    <InvoicesContext.Provider
      value={invoicesSelector({
        state: {
          invoices,
          showInvoicesModal
        },
        actions: {
          openInvoices,
          closeModal
        }
      })}
    >
      {children}
      {showInvoicesModal && (
        <ViewInvoicesModal
          visible={showInvoicesModal}
          readableId={video.readableId}
          projectName={video.name}
          completedAt={getCompletedAt(video.outputs)}
          onClose={closeModal}
          invoices={invoices}
        />
      )}
    </InvoicesContext.Provider>
  );
}
